import styles from './styles.module.css';
import { List as ListIcon } from '@mui/icons-material';
import { LinearProgress, List, ListItem, useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import FlexContainer from '../common/flex-container';
import { fetchDataDynamicMenu } from './api';
import dynamicMenuMapped from './mapping/dynamic-menu-mapped.ts';
import type DynamicMenuItem from './types/dynamic-menu-item.ts';
import classNames from 'classnames';

type SidebarProps = {
  isVisibleMainMenu: boolean;
  onMainMenuVisibleChange: () => void;
};

const Sidebar: React.FunctionComponent<SidebarProps> = (props: SidebarProps) => {
  const location = useLocation();
  const pathname: string = location.pathname;

  const theme: Theme = useTheme();

  const dynamicMenuDataResult = useQuery({
    queryKey: ['dynamic-main-menu'],
    queryFn: () => fetchDataDynamicMenu(),
    gcTime: 10000000,
  });

  const { data, isFetching }: { data: any; isFetching: boolean } = dynamicMenuDataResult;
  const dynamicMenuData: DynamicMenuItem[] = data?.data ? dynamicMenuMapped(data.data) : [];

  return (
    <div
      className={styles['sidebar-container']}
      style={{ display: props.isVisibleMainMenu ? 'block' : 'none' }}
    >
      <FlexContainer height={5} width="100dvw" flexdirection="column">
        {isFetching && <LinearProgress />}
      </FlexContainer>
      {dynamicMenuData?.map((dynamicMenuData: DynamicMenuItem) => {
        return (
          <FlexContainer flexdirection="column" key={dynamicMenuData.name}>
            <div
              className={styles['sidebar-item-header']}
              color={pathname === dynamicMenuData.link ? theme.palette.primary.light : 'transparent'}
            >
              {dynamicMenuData.link ? (
                <Link onClick={props.onMainMenuVisibleChange} to={dynamicMenuData.link}>
                  <FlexContainer alignitems="center" gap={5}>
                    {dynamicMenuData?.image ? (
                      <img
                        src={dynamicMenuData.image}
                        height="24"
                        width="24"
                        alt={dynamicMenuData.name || ''}
                      />
                    ) : (
                      <ListIcon />
                    )}{' '}
                    {dynamicMenuData.name}
                  </FlexContainer>
                </Link>
              ) : (
                <FlexContainer alignitems="center" gap={5}>
                  {dynamicMenuData?.image ? (
                    <img
                      src={dynamicMenuData.image}
                      height="24"
                      width="24"
                      alt={dynamicMenuData.name || ''}
                    />
                  ) : (
                    <ListIcon />
                  )}{' '}
                  {dynamicMenuData.name}
                </FlexContainer>
              )}
            </div>
            {dynamicMenuData?.children?.length && dynamicMenuData.children.length > 0 ? (
              <div className={styles['list-container']}>
                <List>
                  {dynamicMenuData?.children?.map((dynamicMenuData: DynamicMenuItem) => {
                    const listItemContainerStyled: string = classNames({
                      [styles['list-item-container']]: true,
                      [styles['list-item-container--transparent']]: pathname !== dynamicMenuData.link,
                    });
                    return (
                      <ListItem key={dynamicMenuData.name}>
                        <div className={listItemContainerStyled}>
                          {dynamicMenuData.link ? (
                            <Link onClick={props.onMainMenuVisibleChange} to={dynamicMenuData.link}>
                              <FlexContainer alignitems="center" gap={5}>
                                {dynamicMenuData?.image && (
                                  <img
                                    src={dynamicMenuData.image}
                                    height="24"
                                    width="24"
                                    alt={dynamicMenuData.name || ''}
                                  />
                                )}{' '}
                                {dynamicMenuData.name}
                              </FlexContainer>
                            </Link>
                          ) : (
                            <FlexContainer alignitems="center" gap={5}>
                              {dynamicMenuData?.image && (
                                <img
                                  src={dynamicMenuData.image}
                                  height="24"
                                  width="24"
                                  alt={dynamicMenuData.name || ''}
                                />
                              )}{' '}
                              {dynamicMenuData.name}
                            </FlexContainer>
                          )}
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            ) : null}
          </FlexContainer>
        );
      })}
    </div>
  );
};

export default Sidebar;
